// src/components/Stream/StreamHeader.jsx

import React, { useContext, useEffect, useState } from "react";
import { 
  IconButton, 
  Stack, 
  Typography, 
  Popover, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText, 
  Avatar, 
  Chip,
  Box,
  Button
} from "@mui/material";
import CloseIcon from "@mui/icons-material/HighlightOff";
import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BlockIcon from "@mui/icons-material/Block";
import Circle from "@mui/icons-material/Circle";
import MusicNote from "@mui/icons-material/MusicNote";
import CloseFullScreenIcon from "@mui/icons-material/FullscreenExit";
import OpenFullScreenIcon from "@mui/icons-material/Fullscreen";
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import cookieStorage from "../../../services/cookie-storage";
import notifier from "../../Notifier";

import emitter from "../../../EventEmitter";
import { SocketContext } from "../../../context/socket";
import { setStreamCount } from "../../../action/streamCount";
import {
  getAudienceId,
  getUserId,
  getUserDetail,
  putStreamRecord,
  formatJsonToSingleLine
} from "../../../constants/function";
import { Images } from "../../../assets/common";
import Search from "../../../pages/Search";
import ProfileBorder from "../../Header/ProfileBorder";

/**
 * A component that displays:
 * - End stream button
 * - Fullscreen toggle
 * - Hide/Show stream
 * - The stream title
 * - Watchers count (via local watchers state)
 * - The streamCount from Redux
 * - Optional logic to handle “song-started” events, partial increments, etc.
 */
export default function StreamHeader({
  title,          // The title of the stream
  isOwner,        // Is the current user the host?
  isJoined,       // Has the user joined the stream?
  playlistId,     // The channel or playlist ID
  streamData,     // The stream data object
  streamSong,     // Optional: "song-started" logic if needed
  obs,            // Optional: if OBS usage
  totalUsers,
  liveUser,       // Live users from parent component
  isFullScreen,   // Whether we are in fullscreen mode
  leaveStreamSentRef // Ref to track if leave-stream was already sent
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation("common");
  const socket = useContext(SocketContext).Socket;

  // Basic data for partial stream counting
  const token = cookieStorage.getCookie("Authorization");
  const userId = getUserId();

  // Redux states: track info, streamCount, etc.
  const { songs, index, track, pos } = useSelector((state) => state.streamSong);
  const { streamCount } = useSelector((state) => state.streamCount);
  const seek = useSelector((state) => state.seek);
  
  // Get user detail using the same function as main header
  const userDetail = getUserDetail();
  
  // Keep watchers in *local state* so updates re-render the UI
  const [watchers, setWatchers] = useState(liveUser || []);
  const [localWatchers, setLocalWatchers] = useState([]);
  const [watcherCount, setWatcherCount] = useState((liveUser || []).length || (isOwner && isJoined ? 1 : 0));
  
  // User list dropdown state
  const [userListAnchor, setUserListAnchor] = useState(null);
  const isUserListOpen = Boolean(userListAnchor);



  // Local UI states
  const [isHidden, setIsHidden] = useState(false);
  const [songData, setSongData] = useState({});
  const [channelListenIncrement, setChannelListenIncrement] = useState(0);
  const [renderKey, setRenderKey] = useState(0);


  useEffect(() => {
    if (!socket) {
      console.log("[StreamHeader] Socket not available");
      return;
    }

    console.log("[StreamHeader] Setting up socket listeners - socket connected:", socket.connected, "isOwner:", isOwner, "isJoined:", isJoined);

    // Just for reference if you have “livestream-streams” logic
    socket.on("livestream-streams", (data) => {
      if (data.channelName === playlistId) {
        setChannelListenIncrement(data.increment);
      }
    });

    // If host => new count => audience side updates
    socket.on("livestream-streamcount", (data) => {
      if (data.channelName === playlistId && !isOwner) {
        dispatch(setStreamCount(data.streamCount));
      }
    });

    // “song-started” => optional logic
    socket.on("song-started", (data) => {
      if (data.roomId === playlistId) {
        setSongData({ ...data });
      }
    });

    // watchers => store in watchers state
    socket.on("audience-in-stream", handleAudience);
    
    // Debug: log all socket events for this component
    console.log("[StreamHeader] Socket listeners set up. isOwner:", isOwner, "isJoined:", isJoined);

    // Handle raised hands notification (for hosts)
    socket.on("raised", (data) => {
      if (data.channelName === playlistId && isOwner) {
        if (data.raised) {
          notifier.info(`${data.user.firstName} ${data.user.lastName} raised their hand to become co-host`);
        }
      }
    });

    // Handle successful role changes
    socket.on("makeCoHost", (data) => {
      if (data.channelName === playlistId) {
        const userName = data.user?.firstName && data.user?.lastName 
          ? `${data.user.firstName} ${data.user.lastName}` 
          : 'User';
        
        if (data.user.role === "host") {
          notifier.success(`${userName} is now a co-host with video enabled`);
        } else {
          notifier.info(`${userName} is now audience`);
        }
      }
    });

    return () => {
      socket.off("livestream-streams");
      socket.off("livestream-streamcount");
      socket.off("song-started");
      socket.off("audience-in-stream", handleAudience);
      socket.off("raised");
      socket.off("makeCoHost");
    };
  }, [socket, streamSong, playlistId, isOwner]);



  // Update watchers when liveUser prop changes - this takes priority over socket events
  useEffect(() => {
    console.log("[StreamHeader] liveUser effect triggered - liveUser:", liveUser?.length || 0, "isOwner:", isOwner, "isJoined:", isJoined, "currentCount:", watcherCount);
    
    if (liveUser && Array.isArray(liveUser) && liveUser.length > 0) {
      console.log("[StreamHeader] => liveUser prop updated => count:", liveUser.length, "users:", liveUser.map(u => u.user?.firstName || 'Unknown'));
      setWatchers(liveUser);
      setWatcherCount(liveUser.length);
      setRenderKey(prev => prev + 1); // Force re-render
    } else {
      // If no users yet but stream is active, show at least 1 (the host or current user)
      console.log("[StreamHeader] No liveUser data, setting minimum count");
      const minCount = isJoined ? 1 : 0;
      setWatcherCount(minCount);
      setWatchers([]);
    }
  }, [liveUser, isOwner, isJoined]);

  useEffect(() => {
    console.log("[StreamHeader] => watchers updated =>", watchers);
  }, [watchers]);
  
  useEffect(() => {
    console.log("[StreamHeader] => streamCount changed =>", streamCount);
  }, [streamCount]);

  // 1) Fullscreen toggling
  const toggleFullScreen = () => {
    if (isFullScreen) {
      emitter.emit("closeFullScreen");
    } else {
      emitter.emit("openFullScreen");
    }
  };

  // 2) Hide/Show stream
  const hideStream = () => {
    emitter.emit("closeNft");
    const doc = document.getElementById("__streaming_container");
    const streamTippers = document.getElementById("_streamTippers");
    const streaming_control = document.getElementById("_streaming_control");
    const streaming_canvas = document.getElementById("_streaming_canvas");
    if (doc) doc.style.display = "none";
    if (streamTippers) streamTippers.style.display = "none";
    if (streaming_control) streaming_control.style.display = "none";
    if (streaming_canvas) streaming_canvas.style.display = "none";
    setIsHidden(true);
  };

  const showStream = () => {
    emitter.emit("closeNft");
    const doc = document.getElementById("__streaming_container");
    const streamTippers = document.getElementById("_streamTippers");
    const streaming_control = document.getElementById("_streaming_control");
    const streaming_canvas = document.getElementById("_streaming_canvas");
    if (doc) doc.style.display = "block";
    if (streamTippers) streamTippers.style.display = "block";
    if (streaming_control) streaming_control.style.display = "flex";
    if (streaming_canvas) streaming_canvas.style.display = "flex";
    setIsHidden(false);
  };

  // 4) If host => broadcast new streamCount on changes
  useEffect(() => {
    if (socket && isJoined && isOwner) {
      socket.emit("livestream-streamcount", {
        JWT: token,
        streamCount,
        channelName: playlistId,
      });
    }
  }, [streamCount]);

  // 5) Hide/Show stream events
  useEffect(() => {
    emitter.on("hideStream", hideStream);
    emitter.on("showStream", showStream);
    return () => {
      emitter.removeListener("hideStream", hideStream);
      emitter.removeListener("showStream", showStream);
    };
  }, []);
  // Update watcherCount when watchers change
useEffect(() => {
  if (Array.isArray(watchers)) {
    setWatcherCount(watchers.length);
  }
}, [watchers]);

  // 6) If host + joined => hide share screen in /LiveVideoStreaming
  useEffect(() => {
    if (isOwner && isJoined && history.location.pathname.includes("/LiveVideoStreaming")) {
      const shareScreenBtn = document.getElementById("_share_screen_btn");
      if (shareScreenBtn) shareScreenBtn.style.display = "none";
    }
  }, [isJoined]);



  const handleAudience = (data) => {
    console.log("[StreamHeader] handleAudience received:", {
      channelName: data.channelName,
      playlistId: playlistId,
      streamDataId: streamData?._id,
      liveUsersCount: data.liveUsers?.length,
      match: data.channelName === playlistId || data.channelName === streamData?._id,
      isOwner: isOwner,
      isJoined: isJoined,
      currentWatcherCount: watcherCount
    });
    
    // Check both playlistId and streamData._id for matching
    if (data.channelName === playlistId || data.channelName === streamData?._id) {
      // Always update from socket events regardless of owner status
      if (data.liveUsers && Array.isArray(data.liveUsers)) {
        console.log("[StreamHeader] Updating watchers from", watchers.length, "to", data.liveUsers.length);
        setWatchers(data.liveUsers);
        setWatcherCount(data.liveUsers.length);
        
        // Force a re-render to ensure UI updates
        setRenderKey(prev => prev + 1);
      } else {
        // If no users data but we're in a stream, show minimum count
        const minCount = isJoined ? 1 : 0;
        console.log("[StreamHeader] No users data, setting minimum count:", minCount);
        setWatcherCount(minCount);
        setWatchers([]);
      }
  
        // Optionally record “stop/start”
        const playingIndex = localStorage.getItem("streamIndex");
        if (songs.length > 0 && playingIndex && playingIndex >= 0) {
          const currentTimeInSong = parseFloat(localStorage.getItem("curretTimeInSong") || 0);
  
          // "stop" for old watchers
          putRecord(
            "stop",
            songs[playingIndex]._id,
            playlistId,
            playingIndex,
            currentTimeInSong,
            new Date().getTime(),
            songs,
            watchers
          );
  
          // "start" for new watchers
          putRecord(
            "start",
            songs[playingIndex]._id,
            playlistId,
            playingIndex,
            currentTimeInSong,
            new Date().getTime(),
            songs,
            data.liveUsers
          );
        }
    }
  };

  
    useEffect(() => {
      setLocalWatchers(watchers);
    }, [watchers]);
  
    useEffect(() => {
      setRenderKey((prevKey) => prevKey + 1);
    }, [watchers]);



  // If you have a “putRecord” logic for Kinesis or logs
  const putRecord = async (
    event,
    musicId,
    playlistId,
    index,
    timeInSong,
    associationId,
    playlistSongs,
    liveUserData
  ) => {
    const watchersArr = await getAudienceId(liveUserData);
    const payload = {
      eventType: event,
      timestamp: new Date().getTime(),
      userId: getUserId(),
      musicId,
      playlistId,
      livestreamId: playlistId,
      songDetails: playlistSongs[index],
      timeInSong,
      associationId,
      audienceArray: watchersArr,
    };
    putStreamRecord(payload);
  };

  // Handle user list dropdown
  const handleUserListClick = (event) => {
    console.log('👥 handleUserListClick called');
    console.log('👥 Event exists:', !!event);
    console.log('👥 CurrentTarget exists:', !!event?.currentTarget);
    
    if (event && event.currentTarget) {
      console.log('👥 Setting anchor...');
      setUserListAnchor(event.currentTarget);
      console.log('👥 Anchor set successfully');
    } else {
      console.log('👥 Missing event or currentTarget!');
    }
  };

  const handleUserListClose = () => {
    setUserListAnchor(null);
  };

  // Get user role for display
  const getUserRole = (watcher, isOwner) => {
    // Check if watcher has user object with role
    if (watcher.user && watcher.user.role === "host") return "Host";
    // Check if watcher itself has role (for backward compatibility)
    if (watcher.role === "host") return "Host";
    // Check if this is the owner viewing their own stream
    if (isOwner && watcher.user && watcher.user._id === getUserId()) return "Host";
    if (isOwner && watcher._id === getUserId()) return "Host";
    return "Viewer";
  };

  // Handle invite user to co-host
  const handleInviteUser = (user) => {
    if (socket && isOwner) {
      const newRole = user.role === "audience" ? "host" : "audience";
      
      const inviteData = {
        JWT: token,
        channelName: playlistId,
        userId: user._id,
        role: newRole,
        mic: true,  // Enable mic by default for co-hosts
        camera: true,  // Enable camera by default for co-hosts
        mutedBy: null,
        changedToHost: newRole === "host"
      };
      
      socket.emit("stream-invite", inviteData);
      
      // Close the dropdown after action
      handleUserListClose();
      
      // Show immediate feedback
      notifier.info(`${newRole === "host" ? "Promoting" : "Demoting"} ${user.firstName || "User"}...`);
    }
  };

  // Handle kick user
  const handleKickUser = (user) => {
    if (socket && isOwner) {
      socket.emit("kick-user", {
        JWT: token,
        channelName: playlistId,
        userId: user._id
      });
    }
  };

  // Handle tip user
  const handleTipUser = (user) => {
    // Emit event to open tip modal for specific user
    if (user._id !== getUserId()) {
      emitter.emit("openTipModal", { user });
    }
  };

  return (
    <Box
      key={renderKey} 
      sx={{ 
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 10px"
      }}
    >
      {/* LEFT side => SIKI Logo + End stream (owner only), Fullscreen */}
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* SIKI Logo */}
        <img 
          src={require('../../../assets/common/resources/IMG_2158.PNG')}
          alt="SIKI Logo"
          onClick={async (e) => {
            console.log("🚨 NEW SIKI HANDLER RUNNING - VERSION 2.0 🚨");
            
            try {
              console.log("🏠 Conditions check:", {
                socket: !!socket,
                connected: socket?.connected,
                isJoined: isJoined,
                playlistId: playlistId,
                streamDataId: streamData?._id
              });
            
              // Notify backend that user is leaving the stream
              if (socket && socket.connected && isJoined) {
                console.log("🏠 Emitting stream-end event...");
                
                // Mark as sent to prevent duplicate from route listener
                if (leaveStreamSentRef) {
                  leaveStreamSentRef.current = true;
                }
                
                // Emit stream-end and wait for it to process
                const eventData = {
                  JWT: cookieStorage.getCookie("Authorization"),
                  channelName: playlistId || streamData?._id,
                  role: "audience"
                };
                console.log("🏠 Event data:", eventData);
                socket.emit("stream-end", eventData);
                
                console.log("🏠 Event sent, waiting...");
                await new Promise(resolve => setTimeout(resolve, 250));
                console.log("🏠 Done waiting, navigating...");
              } else {
                console.log("🏠 Skipping stream-end (conditions not met)");
              }
              
              // Navigate to home
              console.log("🏠 Navigating to /home");
              history.push("/home");
            } catch (error) {
              console.error("🏠 Error in SIKI logo click:", error);
              history.push("/home");
            }
          }}
          style={{ 
            cursor: "pointer", 
            height: "40px",
            marginRight: "16px",
            marginTop: "-4px",
            display: "block",
            alignSelf: "center"
          }}
        />
        
        {/* Divider */}
        <Box sx={{ height: "30px", width: "1px", backgroundColor: "rgba(255,255,255,0.2)" }} />
        
        {/* End stream button - only for owner */}
        {isOwner && (
          <IconButton
            id="closeIcon"
            title="End stream"
            onClick={() => emitter.emit("endStream")}
          >
            <CloseIcon sx={{ color: "#fff" }} />
          </IconButton>
        )}
        
        {/* Leave stream button - for non-owners */}
        {!isOwner && isJoined && (
          <IconButton
            id="leaveIcon"
            title="Leave stream"
            onClick={async () => {
              console.log("[StreamHeader] Leave stream clicked by non-owner");
              
              try {
                // Notify backend that user is leaving the stream
                if (socket && socket.connected) {
                  console.log("[StreamHeader] Emitting stream-end for audience leave");
                  
                  // Mark as sent to prevent duplicate from route listener
                  if (leaveStreamSentRef) {
                    leaveStreamSentRef.current = true;
                  }
                  
                  // Emit stream-end and wait for it to process
                  const eventData = {
                    JWT: token,
                    channelName: playlistId || streamData?._id,
                    role: "audience"
                  };
                  socket.emit("stream-end", eventData);
                  
                  // Small delay to ensure the socket event is processed
                  await new Promise(resolve => setTimeout(resolve, 250));
                }
                
                // Navigate to home
                history.push("/home");
              } catch (error) {
                console.error("[StreamHeader] Error leaving stream:", error);
                history.push("/home");
              }
            }}
          >
            <CloseIcon sx={{ color: "#fff" }} />
          </IconButton>
        )}

        <IconButton className="btn" onClick={toggleFullScreen}>
          {isFullScreen ? (
            <CloseFullScreenIcon sx={{ color: "#fff" }} />
          ) : (
            <OpenFullScreenIcon sx={{ color: "#fff" }} />
          )}
        </IconButton>
      </Stack>

      {/* MIDDLE => Title + watchers + streamCount */}
      <Stack
        className="_streaming_header_container"
        alignItems="center"
        spacing={1}
        direction="row"
      >
        {/* Optional divider */}
        <div style={{ paddingLeft: "20px", borderLeft: "2px solid #ccc" }} />

        {/* "LIVE" circle */}
        <Circle style={{ color: "green", width: 15, height: 15 }} color="default" />

        {/* Studio/Playlist name */}
        <Typography className="fs-17" sx={{ fontWeight: 500 }}>
          {streamData?.name || streamData?.title || 'Live Stream'}
        </Typography>

        {/* Person icon => watchers from watchers state - clickable */}
        <Stack 
          direction="row" 
          alignItems="center" 
          spacing={0.5}
          onClick={(e) => {
            console.log("🚨 NEW DROPDOWN HANDLER RUNNING - VERSION 2.0 🚨");
            console.log("👥 PERSON ICON CLICKED!");
            console.log("👥 Event:", e.type);
            console.log("👥 Current anchor:", !!userListAnchor);
            console.log("👥 Should open:", !userListAnchor);
            
            try {
              handleUserListClick(e);
            } catch (error) {
              console.error("👥 Error in person icon click:", error);
            }
          }}
          sx={{ 
            cursor: 'pointer', 
            padding: '4px 8px', 
            borderRadius: '4px',
            '&:hover': { 
              backgroundColor: 'rgba(255, 255, 255, 0.1)' 
            }
          }}
        >
          <PersonIcon style={{ color: "#fff" }} />
          <Typography sx={{ color: "#fff" }}>{watcherCount || 0}</Typography>
        </Stack>

        {/* The music note => Redux-based streamCount */}
        <MusicNote sx={{ color: "yellow" }} className="_cp" />
        <span className="color-green-500">{streamCount.toFixed(2)}</span>
      </Stack>

      {/* RIGHT side => Search bar, Wallet and Profile */}
      <Stack direction="row" alignItems="center" spacing={2}>
        {/* Search Bar */}
        <Box sx={{ minWidth: "300px", maxWidth: "400px" }}>
          <Search />
        </Box>
        
        {/* Wallet Icon */}
        <IconButton
          title="Wallet"
          onClick={() => emitter.emit("openWallet")}
          sx={{ color: "#fff" }}
        >
          <AccountBalanceWalletIcon />
        </IconButton>
        
        {/* Profile Avatar with Hexagonal Border */}
        <Box
          id="userProfileIcon"
          onClick={async () => {
            // Notify backend that user is leaving the stream
            if (socket && socket.connected && isJoined) {
              console.log("[StreamHeader] Profile button clicked - notifying backend user is leaving");
              
              // Mark as sent to prevent duplicate from route listener
              if (leaveStreamSentRef) {
                leaveStreamSentRef.current = true;
              }
              
              // Emit stream-end and wait a moment for it to process
              const eventData = {
                JWT: cookieStorage.getCookie("Authorization"),
                channelName: playlistId || streamData?._id,
                role: "audience"
              };
              console.log("[StreamHeader] Profile button - about to emit stream-end with data:", eventData);
              socket.emit("stream-end", eventData);
              
              // Small delay to ensure the socket event is processed
              await new Promise(resolve => setTimeout(resolve, 100));
            }
            
            // Navigate to profile
            history.push("/app/Profile");
          }}
          sx={{ cursor: "pointer", position: "relative" }}
        >
          <ProfileBorder progress={userDetail["percentage"]} />
          <img
            style={{
              zIndex: -10,
              objectFit: "cover",
              position: "absolute",
              width: "42px",
              height: "37px",
              top: "12px",
              right: "24px",
              clipPath: "polygon(25% 0%, 75% 0%, 100% 50%, 75% 99%, 25% 99%, 0% 50%)",
              backgroundColor: "#1e1e1e",
            }}
            src={
              userDetail["profileImg"] &&
              userDetail["profileImg"]["filePath"] != ""
                ? userDetail["profileImg"]["filePath"]
                : ""
            }
            alt="user profile"
          />
        </Box>
      </Stack>
    
    {/* User List Dropdown */}
    {console.log('👥 Popover state:', {
      isOpen: isUserListOpen,
      hasAnchor: !!userListAnchor,
      shouldOpen: isUserListOpen && Boolean(userListAnchor),
      watchersCount: watchers?.length
    })}
    <Popover
      open={isUserListOpen && Boolean(userListAnchor)}
      anchorEl={userListAnchor}
      onClose={handleUserListClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      PaperProps={{
        sx: {
          maxHeight: 400,
          width: 400,
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          zIndex: 99999 // Force dropdown to appear above everything
        }
      }}
      sx={{
        zIndex: 99999 // Also set on the Popover itself
      }}
    >
      <Box sx={{ p: 2 }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#fff', 
            textAlign: 'center', 
            mb: 2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            pb: 1
          }}
        >
          Viewers ({watcherCount})
        </Typography>
        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
          {watchers.map((watcher) => (
            <ListItem 
              key={watcher._id || watcher.uid} 
              sx={{ 
                py: 1,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              <ListItemAvatar>
                <Avatar
                  src={watcher.user?.profileImg?.filePath}
                  sx={{ width: 32, height: 32 }}
                >
                  {watcher.user?.firstName?.charAt(0) || 'U'}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ color: '#fff', fontSize: '14px' }}>
                        {watcher.user?.firstName || 'Unknown'} {watcher.user?.lastName || ''}
                      </Typography>
                      <Chip
                        label={getUserRole(watcher, isOwner)}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '10px',
                          backgroundColor: getUserRole(watcher, isOwner) === 'Host' 
                            ? 'rgba(255, 193, 7, 0.2)' 
                            : 'rgba(76, 175, 80, 0.2)',
                          color: getUserRole(watcher, isOwner) === 'Host' 
                            ? '#ffc107' 
                            : '#4caf50'
                        }}
                      />
                    </Box>
                    {/* Action buttons - only show if current user is owner and it's not themselves */}
                    {isOwner && watcher.user?._id !== getUserId() && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* Tip button */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTipUser(watcher.user);
                          }}
                          sx={{ 
                            padding: '4px',
                            '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                          }}
                          title="Tip User"
                        >
                          <img 
                            src={Images.tip_player} 
                            alt="Tip" 
                            style={{ width: 16, height: 16 }}
                          />
                        </IconButton>
                        
                        {/* Invite/Promote button - icon only */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInviteUser(watcher.user);
                          }}
                          sx={{ 
                            color: watcher.user?.role === 'audience' ? '#ff0000' : '#666',
                            '&:hover': { 
                              backgroundColor: watcher.user?.role === 'audience' ? 'rgba(255, 0, 0, 0.1)' : 'rgba(102, 102, 102, 0.1)' 
                            }
                          }}
                          title={watcher.user?.role === 'audience' ? 'Make Co-Host' : 'Remove Co-Host'}
                        >
                          {watcher.user?.role === 'audience' ? <PersonAddIcon sx={{ fontSize: 16 }} /> : <PersonIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                        
                        {/* Kick button */}
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleKickUser(watcher.user);
                          }}
                          sx={{ 
                            color: '#f44336',
                            '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.1)' }
                          }}
                          title="Kick User"
                        >
                          <BlockIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    )}
                    {/* Show tip button for non-owners */}
                    {!isOwner && watcher.user?._id !== getUserId() && (
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTipUser(watcher.user);
                        }}
                        sx={{ 
                          padding: '4px',
                          '&:hover': { backgroundColor: 'rgba(76, 175, 80, 0.1)' }
                        }}
                        title="Tip User"
                      >
                        <img 
                          src={Images.tip_player} 
                          alt="Tip" 
                          style={{ width: 16, height: 16 }}
                        />
                      </IconButton>
                    )}
                  </Box>
                }
                secondary={
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                    {watcher.platform === 'mobile' ? '📱 Mobile' : '💻 Web'}
                  </Typography>
                }
              />
            </ListItem>
          ))}
          {watchers.length === 0 && (
            <ListItem>
              <ListItemText
                primary={
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
                    No viewers yet
                  </Typography>
                }
              />
            </ListItem>
          )}
        </List>
      </Box>
    </Popover>
    </Box>

  );
}
